import { DateTime } from "luxon";
import React, { useMemo } from "react";

interface TimePointDisplayIndividualProps {
  containerWidth: number;
  visibleStart: number;
  visibleEnd: number;
  offset: number;
  scale: number;
  timezone: string;
}
interface TimeTickLevel {
  durationThresholdMs: number;
  intervalMs: number;
  format: string;
}

const TIME_TICK_INTERVALS: TimeTickLevel[] = [
  {
    durationThresholdMs: 4 * 24 * 60 * 60 * 1000,
    intervalMs: 24 * 60 * 60 * 1000,
    format: "",
  },
  {
    durationThresholdMs: 2 * 24 * 60 * 60 * 1000,
    intervalMs: 6 * 60 * 60 * 1000,
    format: "h",
  },
  {
    durationThresholdMs: 1 * 24 * 60 * 60 * 1000,
    intervalMs: 3 * 60 * 60 * 1000,
    format: "h",
  },
  {
    durationThresholdMs: 6 * 60 * 60 * 1000,
    intervalMs: 1 * 60 * 60 * 1000,
    format: "h",
  },
  {
    durationThresholdMs: 1 * 60 * 60 * 1000,
    intervalMs: 15 * 60 * 1000,
    format: "h:mm",
  },
  {
    durationThresholdMs: 15 * 60 * 1000,
    intervalMs: 5 * 60 * 1000,
    format: "h:mm",
  },
  {
    durationThresholdMs: 5 * 60 * 1000,
    intervalMs: 1 * 60 * 1000,
    format: "h:mm",
  },
  {
    durationThresholdMs: 1 * 60 * 1000,
    intervalMs: 15 * 1000,
    format: "h:mm:ss",
  },
  { durationThresholdMs: 15 * 1000, intervalMs: 5 * 1000, format: "h:mm:ss" },
  { durationThresholdMs: 0, intervalMs: 1000, format: "h:mm:ss" },
];

const TimePointDisplayIndividual: React.FC<TimePointDisplayIndividualProps> = ({
  containerWidth,
  visibleStart,
  visibleEnd,
  timezone,
}) => {
  const visibleDuration = visibleEnd - visibleStart;

  const tickConfig = useMemo(() => {
    for (const level of TIME_TICK_INTERVALS) {
      if (visibleDuration > level.durationThresholdMs) {
        return level;
      }
    }
    return TIME_TICK_INTERVALS[TIME_TICK_INTERVALS.length - 1];
  }, [visibleDuration]);

  const ticks: DateTime[] = useMemo(() => {
    const { intervalMs } = tickConfig;
    const start = DateTime.fromMillis(visibleStart).setZone(timezone);
    const end = DateTime.fromMillis(visibleEnd).setZone(timezone);

    const startTime = start.startOf("day");

    const tickList: DateTime[] = [];
    let cursor = startTime;
    while (cursor.toMillis() <= end.toMillis()) {
      if (cursor.toMillis() >= start.toMillis()) tickList.push(cursor);
      cursor = cursor.plus({ milliseconds: intervalMs });
    }

    return tickList;
  }, [visibleStart, visibleEnd, tickConfig, timezone]);

  return (
    <div
      className='relative w-[calc(100%_-_28px)] flex text-xs text-gray-600 mb-6 h-9'
      data-testid='timePoint'
    >
      <div className='absolute top-1 left-[-50px] text-black text-xs font-bold'>
        <span>
          {DateTime.fromMillis(visibleStart)
            .setZone(timezone)
            .toFormat("LLL-dd")
            .toUpperCase()}
        </span>
      </div>
      {ticks.map((tick, index) => {
        const ms = tick.toMillis();
        const tickPx =
          ((ms - visibleStart) / (visibleEnd - visibleStart)) * containerWidth;

        const isMidnight =
          tick.hour === 0 && tick.minute === 0 && tick.second === 0;
        const isMonthStart = isMidnight && tick.day === 1;
        const isFirstTick = index === 0;
        const isDayInterval = tickConfig.intervalMs >= 24 * 60 * 60 * 1000;

        let label: string;
        const bold = isMidnight && !isDayInterval;

        if (isMidnight) {
          // Show day label
          label = tick.toFormat("LLL-dd").toUpperCase();
        } else {
          // Time formatting only
          const currentMeridiem = tick.toFormat("a");
          const prevMeridiem =
            index > 0 ? ticks[index - 1].toFormat("a") : undefined;
          const showMeridiem =
            tickConfig.format.startsWith("h") &&
            (isFirstTick || currentMeridiem !== prevMeridiem);

          label = tick
            .toFormat(
              showMeridiem
                ? `h${tickConfig.format.slice(1)}a`
                : tickConfig.format,
            )
            .toUpperCase();
        }

        const lineHeight = bold ? "h-5" : "h-3";
        const lineColor = bold ? "bg-black" : "bg-gray-400";

        return (
          <div
            key={index}
            className='absolute flex flex-col items-center -translate-x-1/2 text-center text-black justify-end h-full'
            style={{
              left: `${(tickPx / containerWidth) * 100}%`,
            }}
          >
            <div
              className={`${
                isMonthStart ? "w-0.5" : "w-[1px]"
              } ${lineHeight} ${lineColor}`}
            />
            <span className={`text-xs whitespace-nowrap ${bold ? "font-bold" : ""}`}>
              {label}
            </span>
          </div>
        );
      })}

      {/* horizontal line */}
      <div className='absolute bottom-4 left-0 w-full h-px bg-gray-400' />
    </div>
  );
};

export default TimePointDisplayIndividual;
